import { type MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import ProductCard from '_components/ProductStrip/ProductCard';
import MiniProductCard from '_components/UI/Cards/MiniProductCard';
import QuickAdd from '_components/QuickAdd';
import { getDisplayTags } from '_components/Product/ProductMetaInfo/ProductMetaTags/ProductMetaTags';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { useHitSwatches } from '@hooks/useHitSwatches';
import { useLocale } from '@hooks/useLocale';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import { isGiftCardHit } from '@utils/algolia/isGiftCardHit';
import type { BundleStripProduct } from '@adapters/prismic/transforms/bundleStrip';
import type { AtcSource } from '@utils/cart/atcSource';

import styles from './BundleProductCard.module.scss';

export interface BundleProductPrice {
  displayPrice: string;
  salePrice: string;
}

export interface BundleProductCardProps extends BundleStripProduct {
  coOpBrand: string | null;
  atcSource: AtcSource;
  bundlePrice?: BundleProductPrice;
  onClick?: (e: MouseEvent<HTMLAnchorElement>, productUrl: string) => void;
}

const BundleProductCard = ({
  product,
  variantSku,
  coOpBrand,
  atcSource,
  bundlePrice,
  onClick,
}: BundleProductCardProps) => {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const initialColour = useMemo(
    () => colourKeyForVariantSku(product, variantSku),
    [product, variantSku]
  );

  const { activeVariant, priceGroupKey } = useHitSwatches(
    initialColour,
    product.variants,
    {
      price: product.price,
      salePrice: product.salePrice,
      image: product.image,
      imageTransparent: product.imageTransparent,
      badge: product.badge ?? null,
      badgeGuest: product.badgeGuest ?? null,
      sku: product.sku,
    },
    locale
  );

  const rawBadge =
    product.isMemberPrice && priceGroupKey !== 'guest'
      ? activeVariant?.badge
      : activeVariant?.badgeGuest;

  const queryString = new URLSearchParams(
    activeVariant?.colourQuerySlug ?? ''
  ).toString();
  const productUrl = `${product.url}${queryString ? `?${queryString}` : ''}`;

  const image =
    activeVariant?.imageTransparent ||
    activeVariant?.image ||
    product.imageTransparent ||
    product.image ||
    '/images/pdp/product-placeholder.jpg';
  const displayPrice =
    bundlePrice?.displayPrice ||
    activeVariant?.priceRange?.price ||
    activeVariant?.priceFormatted ||
    '';
  const salePrice =
    bundlePrice?.salePrice ||
    activeVariant?.priceRange?.salePrice ||
    activeVariant?.salePriceFormatted ||
    '';
  const isOnSaleOrClearance =
    Boolean(salePrice) || rawBadge?.code === 'CLEARANCE';

  const tags = useMemo(
    () => [
      ...(product.isNew
        ? [
            {
              normalizedSource: 'promo-new',
              name: t('bundleStrip.newTag'),
              color: 'blue' as const,
            },
          ]
        : []),
      ...getDisplayTags(product.tags),
    ],
    [product.isNew, product.tags, t]
  );

  const activeColourKey = activeVariant?.swatchName ?? product.colour;
  const activeColourVariant = product.variants?.[activeColourKey];
  const showQuickAdd =
    FEATURE_FLAG_QUICK_ADD &&
    activeColourVariant?.availableOnlinePurchase !== false &&
    !isGiftCardHit(product.parentSku);

  const quickAdd = (compact: boolean) =>
    showQuickAdd ? (
      <QuickAdd
        compact={compact}
        productCode={product.parentSku}
        productTitle={product.title}
        productUrl={productUrl}
        initialColour={{
          colourName: activeColourKey,
          colourSku: activeVariant?.sku ?? product.sku,
        }}
        variantCount={activeColourVariant?.variantCount}
        analytics={{
          source: 'bundle',
          itemListName: atcSource,
          coOpBrand,
          atcSource,
        }}
      />
    ) : null;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => onClick?.(e, productUrl),
    [onClick, productUrl]
  );

  return (
    <>
      <div className={styles.desktop}>
        <ProductCard
          productImage={image}
          brand={product.brand || ''}
          displayName={product.title || ''}
          productUrl={productUrl}
          displayPrice={displayPrice}
          salePrice={salePrice}
          isOnSaleOrClearance={isOnSaleOrClearance}
          discountPercentage={bundlePrice ? 0 : rawBadge?.discount_percentage}
          multiDiscount={bundlePrice ? false : rawBadge?.multi_discount}
          memberBadgeCode={
            !bundlePrice && rawBadge?.is_member_price
              ? rawBadge?.code
              : undefined
          }
          tags={tags}
          isCanadianBrand={product.isCanadianBrand}
          isMadeInCanada={product.isMadeInCanada}
          productCode={product.parentSku}
          trackClick={handleClick}
          quickAdd={quickAdd(false)}
          showRatings={false}
          showComparison={false}
        />
      </div>
      <div className={styles.mobile}>
        <MiniProductCard
          productUrl={productUrl}
          productName={product.title || ''}
          imageSrc={image}
          displayPrice={displayPrice}
          salePrice={salePrice || undefined}
          cartAction={quickAdd(true)}
          onClick={handleClick}
        />
      </div>
    </>
  );
};

export default BundleProductCard;
